import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import cx from 'classnames';
import s from '../../controllers/Step2Step3Ctrl/step2Step3Ctrl.module.css';
import { ReturnsTooltip } from '../../directives/NewReturnsTooltip';
import { Breadcrumb } from '../../components/Breadcrumb';
import { ReturnSummary } from '../../components/ReturnSummary';
import { ShippingAddress } from '../../components/ShippingAddress';
import { OptionsList } from '../../components/OptionsList';
import { PaymentForm } from '../../components/PaymentForm';
import Attestation from '../Attestation';
import PickupDetails from '../PickupDetails';
import CarrierPickupDetails from '../CarrierPickupDetails';
import { GiftBox } from '../../components/GiftBox';
import DropoffDetails from '../DropoffDetails';
import PreferencesSignUp from '../PreferencesSignUp';
import { ReturnsButton } from '../../components/ReturnsButton';
import BackButton from '../BackButton';
import Footer from '../../components/Footer';
import { PICKUP } from '../../constants/returnMethods';
import {
  returnItemsService,
  currencyService,
  blindReturnSidebarService,
  returnFeeService,
} from '../../services';
import { SignatureAttestation } from '../SignatureAttestation';
import { Language } from '../Language';
import { ActionTypes } from '../../constants/enums';
import ExchangeSummary from '../../components/ExchangeSummary';
import { ResolutionPicker } from '../ResolutionPicker';
import patternInputValidation from '../../lib/patternInputValidation';
import { updateGiftEmail } from '../../actions/page';
import { EMAIL_ADDRESS_PATTERN } from '../../constants/validationPatterns';

const mapStateToProps = ({
  language,
  page: { productType, giftOrder, loading, showAddressOnStep3, captureUserEmail, giftEmail },
  addresses: { shippingAddress } = {},
  returnOptions: {
    selectedOptionId,
    selectedOptionMethodKey,
    returnOptionMethods: {
      [selectedOptionMethodKey]: { return_cost: { service_fee: serviceFee } = {} } = {},
    } = {},
  } = {},
  refunds: { selectedType } = {},
  returnsSettings: {
    [productType]: {
      refunds: { enable_api: refundApiEnabled = false } = {},
      resolutions: { options: resolutionsConfig, enabled: resolutionsEnabled } = {},
      refund_option: refundOptionSettings,
      agreement_checkbox: agreementCheckbox,
      attestation,
      irreparable_option: irreparableOptionSettings,
      product_options: productOptions,
    } = {},
  },
  returnMethods: { selectedMethodKey } = {},
  stripe: { completedPayment, fee: stripeFee } = {},
  pickup: { form: pickupForm },
  storeLocator: { selectedLocation },
  userPreferences: {
    isSigningUp: userPrefIsSigningUp,
    isSaving: userPrefIsSaving,
    validForm: userPrefValidForm,
    skipEmailInput: userPrefSkipEmailInput,
  } = {},
  claims: { enabled: claimsEnabled },
  returnItems: { cartActions = [] },
  unevenExchanges: { isUnevenExchangeSubmit } = {},
  resolutions: { formCompleted: resolutionFormCompleted },
}) => ({
  language,
  giftOrder,
  shippingAddress,
  showAddressOnStep3,
  selectedOptionId,
  refundOptionSettings,
  irreparableOptionSettings,
  selectedType,
  refundApiEnabled,
  selectedMethodKey,
  selectedOptionMethodKey,
  agreementCheckbox,
  attestation,
  serviceFee,
  completedPayment,
  stripeFee,
  pickupForm,
  selectedLocation,
  loading,
  userPrefIsSaving,
  incompleteUserPrefSignUp:
    userPrefIsSigningUp && userPrefSkipEmailInput
      ? false
      : userPrefIsSigningUp && !userPrefValidForm,
  exchangesEnabled: productOptions?.indexOf('exchange') !== -1,
  productType,
  claimsEnabled,
  cartActions,
  isUnevenExchangeSubmit,
  resolutionFormCompleted,
  resolutionsConfig,
  resolutionsEnabled,
  captureUserEmail,
  giftEmail,
});

const ReviewPageBase = ({
  dispatch,
  language,
  giftOrder,
  shippingAddress,
  selectedOptionId,
  refundOptionSettings,
  irreparableOptionSettings,
  selectedType,
  refundApiEnabled,
  selectedMethodKey,
  selectedOptionMethodKey,
  agreementCheckbox,
  attestation,
  serviceFee,
  completedPayment,
  stripeFee,
  pickupForm,
  selectedLocation,
  loading,
  userPrefIsSaving,
  incompleteUserPrefSignUp,
  exchangesEnabled,
  productType,
  expressUpgradeFee,
  onAgreementCheckboxChange,
  showAddressOnStep3,
  paymentIsDue,
  navigateToStep,
  onRefundOptionChange,
  onStripeInit,
  paymentSubmissionError,
  onIrreparableChange,
  onExchangeDisclaimerChange,
  giftReturnsOnChange,
  toStep2,
  displayReturnSubmitErrorMsg,
  returnSubmitErrorMsg,
  processReturn,
  handleReviewBack,
  isDisabledStep3,
  claimsEnabled,
  cartActions,
  isUnevenExchangeSubmit,
  resolutionFormCompleted,
  resolutionsConfig,
  resolutionsEnabled,
  captureUserEmail,
  giftEmail,
}) => {
  const handlingFee = returnFeeService.getHandlingFee();
  const creditReversalAmount = returnFeeService.getCreditReversalAmount();
  const recyclingFeeReversal = returnFeeService.getRecyclingFeeReversal();
  const itemsSubtotal = returnFeeService.getReturnSubtotal();
  const netCost = returnFeeService.getNetCost();
  const discountReversal = returnFeeService.getDiscountReversal();
  const nonMonetaryAttributes = returnFeeService.getNonMonetaryAttributes();
  const restockingFee = returnFeeService.getRestockingFee();
  const returnCost = returnFeeService.getReturnCost();
  const returnCredit = returnFeeService.getReturnCredit();
  const returnTax = returnFeeService.getReturnTax();
  const estimatedRefund = returnFeeService.getEstimatedRefund();
  const isClaim =
    cartActions.length > 0 && cartActions?.every((action) => action === ActionTypes.Claim);
  const [attestationIncomplete, setAttestationIncomplete] = useState(claimsEnabled && isClaim);
  const hasBlindItems = Object.keys(blindReturnSidebarService.getBlindItems()).length > 0;
  const isExchangesOnly = returnItemsService.isExchangesOnly();
  const hasAnExchange = returnItemsService.hasAnExchange();
  const isShippingAddressEmpty = Object.keys(shippingAddress).length === 0;
  const carrierPickupEnabled = selectedOptionId === PICKUP;
  const showShippingAddress =
    showAddressOnStep3 && shippingAddress && !isShippingAddressEmpty && !carrierPickupEnabled;
  const showRefundOptions =
    !isExchangesOnly &&
    (estimatedRefund > 0 ||
      (refundOptionSettings?.enabled && selectedType === '') ||
      refundApiEnabled ||
      !isDisabledStep3);
  const showPayment = paymentIsDue();
  const showStep3Payment = serviceFee > 0 && completedPayment;
  const resolutionFormIncomplete = claimsEnabled && resolutionsEnabled && !resolutionFormCompleted;
  const captureEmailIncomplete =
    captureUserEmail && !patternInputValidation(giftEmail, EMAIL_ADDRESS_PATTERN);

  return (
    <React.Fragment>
      <div className={s.step_3_inner_container}>
        <new-returns-tooltip class={s.return_post_tooltip}>
          <ReturnsTooltip
            closeIcon
            name="returnPostErrorTooltip"
            content={language['returns.step_3.return_post_error_tooltip']}
          />
        </new-returns-tooltip>
        <ReturnsTooltip
          closeIcon
          name="claimPostErrorTooltip"
          content={language['step_3.claim_post_error_tooltip']}
        />

        <Breadcrumb navigateToStep={navigateToStep} currentStep={3} />

        <div className={s.step_3_header_container}>
          <Language
            tagName="h2"
            className="step-3-header"
            langKey={[
              cartActions?.length === 1 ? `returns.step_3.step_3_header.${cartActions[0]}` : null,
              'returns.step_3.step_3_header',
            ].filter(Boolean)}
            aria-hidden="true"
          />
          <Language
            className="step-3-subheader"
            tagName="h5"
            langKey={`returns.step_3.step_3_subheader_${selectedMethodKey}`}
            // eslint-disable-next-line jsx-a11y/aria-role
            role="text"
          />
        </div>
        <div className={cx(s.step_3_return_summary_block, 'return-summary-block-container')}>
          {isUnevenExchangeSubmit ? (
            <ExchangeSummary
              onStripeInit={onStripeInit}
              paymentSubmissionError={paymentSubmissionError}
            />
          ) : (
            <return-summary>
              <ReturnSummary
                product={productType}
                returnCredit={returnCredit}
                restockingFee={restockingFee}
                returnTax={returnTax}
                returnCost={productType === 'returns' && returnCost ? `-${returnCost}` : returnCost}
                returnSubtotal={itemsSubtotal}
                estimatedRefund={estimatedRefund}
                expressUpgradeFee={expressUpgradeFee}
                handlingFee={handlingFee}
                creditReversalAmount={creditReversalAmount}
                recyclingFeeReversal={recyclingFeeReversal}
                netCost={netCost}
                discountReversal={discountReversal}
                nonMonetaryAttributes={nonMonetaryAttributes}
              />
            </return-summary>
          )}
        </div>
        {showShippingAddress && (
          <div>
            <shipping-address>
              <ShippingAddress />
            </shipping-address>
          </div>
        )}
        <ResolutionPicker
          {...{
            showRefundOptions,
            refundApiEnabled,
            refundOptionSettings,
            onRefundOptionChange,
            resolutionsEnabled: claimsEnabled,
            resolutionsConfig,
          }}
        />
        {showPayment && (
          <div>
            <payment-form>
              <PaymentForm onInit={onStripeInit} />
            </payment-form>

            <div
              className={s.payment_flow_errors}
              role="alert"
              dangerouslySetInnerHTML={{ __html: paymentSubmissionError }}
            />
          </div>
        )}

        {irreparableOptionSettings && (
          <div>
            <options-list>
              <OptionsList
                listTitle={language['returns.step_3.irreparable_options']}
                listOptions={irreparableOptionSettings?.options}
                listOnChange={onIrreparableChange}
                listType={irreparableOptionSettings?.type}
              />
            </options-list>
          </div>
        )}
        {exchangesEnabled && hasAnExchange && (
          <div>
            <options-list>
              <OptionsList
                listTitle={
                  giftOrder
                    ? language['returns.step_3.exchanges_gift_disclaimer_title']
                    : language['returns.step_3.exchanges_disclaimer_title']
                }
                listOptions={[
                  {
                    default: false,
                    text: language['returns.step_3.exchanges_disclaimer_agreement'],
                    key: 'exchange_disclaimer',
                    value: 'true',
                    post_data: false,
                  },
                ]}
                listType="checkbox"
                listDescription={
                  giftOrder
                    ? language['returns.step_3.exchanges_gift_disclaimer_text']
                    : language['returns.step_3.exchanges_disclaimer_text']
                }
                listOnChange={onExchangeDisclaimerChange}
              />
            </options-list>
          </div>
        )}
        {agreementCheckbox?.enabled && (
          <div>
            <options-list>
              <OptionsList
                listTitle={language['returns.step_3.agreement_checkbox.title']}
                listOptions={agreementCheckbox?.options}
                listType="checkbox"
                listOnChange={onAgreementCheckboxChange}
              />
            </options-list>
          </div>
        )}

        {attestation?.enabled && <Attestation />}

        {showStep3Payment && (
          <div className={s.step_3_section_container}>
            <h4 className={cx(s.step_3_payment_title, 'step-3-payment-title')}>
              {language[`returns.step_3.${selectedOptionMethodKey}.payment_title`] ||
                language['returns.step_3_payment_title'] ||
                ''}
            </h4>
            <div className={s.step_3_section_description_container}>
              <p
                className={s.step_3_section_key}
                dangerouslySetInnerHTML={{
                  __html:
                    language[`returns.step_3.${selectedOptionMethodKey}.payment_summary_key`] ||
                    language['returns.step_3_payment_summary_key'] ||
                    '',
                }}
              />
              <p
                className={s.step_3_section_value}
                dangerouslySetInnerHTML={{ __html: currencyService.formatPrice(stripeFee) }}
              />
            </div>
          </div>
        )}
        {pickupForm?.date && (
          <div>
            <pickup-details>
              <PickupDetails />
            </pickup-details>
          </div>
        )}

        {carrierPickupEnabled && (
          <div>
            <carrier-pickup-details>
              <CarrierPickupDetails />
            </carrier-pickup-details>
          </div>
        )}

        <div>
          {giftOrder && (
            <div>
              <gift-box>
                <GiftBox onChange={giftReturnsOnChange} />
              </gift-box>
            </div>
          )}

          {captureUserEmail && (
            <GiftBox onChange={(emailValue) => dispatch(updateGiftEmail(emailValue))} />
          )}

          {selectedLocation && (
            <dropoff-details>
              <DropoffDetails toStep2={toStep2} />
            </dropoff-details>
          )}

          <preferences-sign-up>
            <PreferencesSignUp />
          </preferences-sign-up>

          {isClaim && (
            <SignatureAttestation
              titleText={{ langKey: 'step_3.signature_attestation.title' }}
              descriptionText={{ langKey: 'step_3.signature_attestation.description' }}
              signatureHeader={{ langKey: 'step_3.signature_attestation.signature_header' }}
              checkboxText={{ langKey: 'step_3.signature_attestation.checkbox_text' }}
              placeholder={{ langKey: 'step_3.signature_attestation.placeholder' }}
              onChange={(e) => {
                setAttestationIncomplete(!(e.checked && e.name));
              }}
              backdrop
            />
          )}

          <div className={cx(s.terms_button_container, 'terms-button-container')}>
            {displayReturnSubmitErrorMsg && (
              <p
                role="alert"
                className={s.disabled_button_error}
                dangerouslySetInnerHTML={{ __html: returnSubmitErrorMsg }}
              />
            )}
            <div className={s.button_container}>
              <returns-button>
                <ReturnsButton
                  tier="primary"
                  big
                  loading={loading || userPrefIsSaving}
                  onClick={() => processReturn()}
                  disabled={
                    isDisabledStep3 ||
                    loading ||
                    userPrefIsSaving ||
                    incompleteUserPrefSignUp ||
                    attestationIncomplete ||
                    resolutionFormIncomplete ||
                    captureEmailIncomplete
                  }
                >
                  {language['returns.step_3.confirm_button_text']}
                </ReturnsButton>
              </returns-button>

              {!loading && !userPrefIsSaving && (
                <back-button>
                  <BackButton onClick={handleReviewBack} />
                </back-button>
              )}
            </div>
            <span
              role="heading"
              aria-level="5"
              className={cx(s.terms_button_text, 'terms_button_text')}
              dangerouslySetInnerHTML={{ __html: language['returns.step_3.refund_disclaimer'] }}
            />
            {giftOrder && language['returns.step_3.refund_disclaimer_gift'] && (
              <span
                className={cx(s.terms_button_text, 'terms_button_text')}
                dangerouslySetInnerHTML={{
                  __html: language['returns.step_3.refund_disclaimer_gift'],
                }}
                role="heading"
                aria-level="5"
              />
            )}
            {hasBlindItems && (
              <span
                role="heading"
                aria-level="5"
                className={s.terms_button_text}
                dangerouslySetInnerHTML={{
                  __html: language['returns.step_3.blind_item_disclaimer'],
                }}
              />
            )}
          </div>
        </div>
      </div>
      <returns-footer className={s.footer}>
        <Footer />
      </returns-footer>
    </React.Fragment>
  );
};

ReviewPageBase.defaultProps = {
  agreementCheckbox: undefined,
  attestation: undefined,
  serviceFee: undefined,
  refundOptionSettings: undefined,
  irreparableOptionSettings: undefined,
  expressUpgradeFee: undefined,
  paymentSubmissionError: undefined,
  claimsEnabled: false,
  isUnevenExchangeSubmit: undefined,
};

ReviewPageBase.propTypes = {
  dispatch: PropTypes.func.isRequired,
  language: PropTypes.object.isRequired,
  giftOrder: PropTypes.bool.isRequired,
  shippingAddress: PropTypes.object.isRequired,
  selectedOptionId: PropTypes.string.isRequired,
  refundOptionSettings: PropTypes.object,
  irreparableOptionSettings: PropTypes.object,
  selectedType: PropTypes.string.isRequired,
  refundApiEnabled: PropTypes.bool.isRequired,
  selectedMethodKey: PropTypes.string.isRequired,
  selectedOptionMethodKey: PropTypes.string.isRequired,
  expressUpgradeFee: PropTypes.number,
  onAgreementCheckboxChange: PropTypes.func.isRequired,
  agreementCheckbox: PropTypes.object,
  attestation: PropTypes.object,
  serviceFee: PropTypes.number,
  completedPayment: PropTypes.bool.isRequired,
  stripeFee: PropTypes.string.isRequired,
  pickupForm: PropTypes.object.isRequired,
  selectedLocation: PropTypes.object.isRequired,
  loading: PropTypes.bool.isRequired,
  userPrefIsSaving: PropTypes.bool.isRequired,
  incompleteUserPrefSignUp: PropTypes.bool.isRequired,
  exchangesEnabled: PropTypes.bool.isRequired,
  productType: PropTypes.oneOf(['returns', 'repairs']).isRequired,
  showAddressOnStep3: PropTypes.bool.isRequired,
  paymentIsDue: PropTypes.func.isRequired,
  navigateToStep: PropTypes.func.isRequired,
  onRefundOptionChange: PropTypes.func.isRequired,
  onStripeInit: PropTypes.func.isRequired,
  paymentSubmissionError: PropTypes.string,
  onIrreparableChange: PropTypes.func.isRequired,
  onExchangeDisclaimerChange: PropTypes.func.isRequired,
  giftReturnsOnChange: PropTypes.func.isRequired,
  toStep2: PropTypes.func.isRequired,
  displayReturnSubmitErrorMsg: PropTypes.bool.isRequired,
  returnSubmitErrorMsg: PropTypes.string.isRequired,
  processReturn: PropTypes.func.isRequired,
  handleReviewBack: PropTypes.func.isRequired,
  isDisabledStep3: PropTypes.bool.isRequired,
  claimsEnabled: PropTypes.bool,
  cartActions: PropTypes.array.isRequired,
  isUnevenExchangeSubmit: PropTypes.bool,
  resolutionFormCompleted: PropTypes.bool.isRequired,
};

export const ReviewPage = connect(mapStateToProps)(ReviewPageBase);
